import { createReducer, on } from '@ngrx/store';
import { initialState, State } from '../properties-table/properties.state';
import {
    addAttachment,
    addReportParts,
    addSubPart,
    completeReportPart,
    completeSubPart,
    createReport,
    editTitle,
    failReportPart,
    failSubPart,
    refineReportParts,
    removeReportParts,
    removeSubPart,
    sortReport,
    startSubPart,
    updateCover,
    updateFavorites,
    updateMenuPrintability,
    updatePreset,
    updateReportPart
} from './report.actions';
import { Report, ReportPart, ReportPartDef } from './report.model';


const addPartsToReport = (report: Report, parts: ReportPartDef[]) => {
    const pendingParts = parts.map(p => ({
        key: p.key,
        taskId: p.taskId,
        partId: p.partId,
        args: p.args,
        status: 'pending' as const,
        subParts: {},
    }));
    // update already existings parts
    const newParts = report.parts.map(ps => {
        const replace = pendingParts.find(part => part.key === ps.key);
        if (typeof replace !== 'undefined') {
            return replace;
        }
        return ps;
    });
    // otherwise append all non-existent parts
    pendingParts.forEach(p => {
        if (typeof newParts.find(part => part.key === p.key) === 'undefined') {
            // but move SummarySheet at the beginning if it wasn't in report yet
            if (p.key === 'SummarySheetDataSource') {
                newParts.unshift(p);
            } else {
                newParts.push(p);
            }
        }
    });
    return {
        ...report,
        parts: newParts,
    };
};


const mapReport = (state: State, reportId: string, update: (p: Report) => Report) => ({
    ...state,
    properties: state.properties.map(p => p.report?.id !== reportId ? p : { ...p, report: update(p.report) }),
});


const mapReportPart = (state: State, partId: string, update: (p: ReportPart) => ReportPart) => ({
    ...state,
    properties: state.properties.map(p =>
        p.report?.parts.some(i => i.partId === partId)
            ? {
                ...p,
                report: {
                    ...p.report,
                    parts: p.report.parts.map(i => i.partId === partId ? update(i) : i),
                },
            }
            : p
    ),
});


export const reportReducer = createReducer(
    initialState,
    on(createReport, (state, { upid, report }) => ({
        ...state,
        properties: state.properties.map(p => p.parcel.upid === upid ? { ...p, report } : p),
    })),
    on(editTitle, (state, { reportId, title }) => mapReport(
        state,
        reportId,
        r => ({ ...r, title }))
    ),
    on(updateCover, (state, { reportId, propertyImage, coverType, showGoogleImages }) => mapReport(
        state,
        reportId,
        r => ({ ...r, propertyImage, coverType, showGoogleImages })
    )),
    on(addReportParts, refineReportParts, (state, { reportId, parts }) => mapReport(
        state,
        reportId,
        r => addPartsToReport(r, parts)
    )),
    on(completeReportPart, (state, { partId, taskId, data }) => mapReportPart(
        state,
        partId,
        p => p.taskId === taskId && p.status === 'pending' ? { ...p, status: 'complete', data } : p
    )),
    on(updateReportPart, (state, action) => mapReportPart(
        state,
        action.partId,
        () => ({ ...action, type: undefined })
    )),
    on(failReportPart, (state, { partId, taskId, error, errorMessage }) => mapReportPart(
        state,
        partId,
        p => p.taskId === taskId && p.status === 'pending' ? { ...p, status: 'failed', error, errorMessage } : p
    )),
    on(removeReportParts, (state, { reportId, dataSourceKeys }) => mapReport(
        state,
        reportId,
        r => ({ ...r, parts: r.parts.filter(p => !dataSourceKeys.includes(p.key)) })
    )),
    on(addSubPart, (state, { partId, key }) => mapReportPart(
        state,
        partId,
        p => ({ ...p, subParts: { ...p.subParts, [key]: { status: 'pending' } } })
    )),
    on(startSubPart, (state, { partId, key, taskId }) => mapReportPart(
        state,
        partId,
        p => ({ ...p, subParts: { ...p.subParts, [key]: { taskId, status: 'pending' } } })
    )),
    on(completeSubPart, (state, { partId, key, taskId, data }) => mapReportPart(
        state,
        partId,
        p => p.subParts[key]?.taskId === taskId && p.subParts[key]?.status === 'pending'
            ? { ...p, subParts: { ...p.subParts, [key]: { taskId, status: 'complete', data } } }
            : p
    )),
    on(failSubPart, (state, { partId, taskId, error, errorMessage }) => mapReportPart(
        state,
        partId,
        p => Object.values(p.subParts).some(s => s.taskId === taskId && s.status === 'pending')
            ? {
                ...p,
                subParts: Object.fromEntries(Object.entries(p.subParts).map(([k, v]) =>
                    [k, v.taskId === taskId && v.status === 'pending' ? { taskId, status: 'failed', error, errorMessage } : v]
                )),
            }
            : p
    )),
    on(removeSubPart, (state, { partId, key }) => mapReportPart(
        state,
        partId,
        p => p.subParts[key] ? { ...p, subParts: Object.fromEntries(Object.entries(p.subParts).filter(([k, _]) => k !== key)) } : p
    )),
    on(updateMenuPrintability, (state, { menuKey, printable }) => ({
        ...state,
        properties: state.properties.map(p => p.report
            ? { ...p, report: { ...p.report, menu: p.report.menu.map(i => i.key === menuKey ? { ...i, printDisabled: !printable } : i) } }
            : p
        ),
    })),
    on(updateFavorites, (state, { favorites }) => ({
        ...state,
        properties: state.properties.map(p => p.report ? { ...p, report: { ...p.report, favorites } } : p),
    })),
    on(addAttachment, (state, { reportId, attachment }) => mapReport(
        state,
        reportId,
        r => ({ ...r, attachments: [...r.attachments, attachment] })
    )),
    on(sortReport, (state, { reportId, order }) => mapReport(
        state,
        reportId,
        r => ({ ...r, parts: [...r.parts].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key)) })
    )),
    on(updatePreset, (state, { reportId, dataSourceKey, columns }) => ({
        ...state,
        properties: state.properties.map(p => p.report && (typeof reportId === 'undefined' || p.report.id === reportId)
            ? { ...p, report: { ...p.report, presets: { ...p.report.presets, [dataSourceKey]: columns } } }
            : p
        ),
    }))
);
